import { useContext, useState, useEffect, useRef } from "react";
import { GlobalContext } from "../../common/Context";
import LoadingModal from "../../common/LoadingModal";
import { Box, Typography } from "@mui/material";
import SRCLeaderboard from "./components/SRCLeaderboard";
import moment from "moment";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import GrandPrizeArea from "./components/GrandPrizeArea/GrandPrizeArea";
import LoadingWheel from "../../common/LoadingWheel";
import { white } from "../../../theme/colours";
import ErrorNoCompPage from "../../common/ErrorNoCompPage";
import { SRC_NO_COMP_ERROR } from "../../../constants/ids";

const SafeRoads = () => {
  const { userState, firebaseState, competitionList, mobileView, tabletView } =
    useContext(GlobalContext);
  const { t } = useTranslation("SafeRoads");
  const bigScreen = useMediaQuery("(min-height:800px)");

  const [currCompetition, setCurrCompetition] = useState();
  const [rankings, setRankings] = useState();
  const [cachedRankings, setCachedRankings] = useState({});
  const [currTeam, setCurrTeam] = useState();
  const [currStat, setCurrStat] = useState({ id: "total", name: "Total" });
  const [currTier, setCurrTier] = useState();
  const [fullUserTiers, setFullUserTiers] = useState();
  const [sponsorLogos, setSponsorLogos] = useState();
  const [banners, setBanners] = useState();
  const [currentBanners, setCurrentBanners] = useState({
    main: null,
    prize: null,
    team: null,
  });
  const [bannersLoading, setBannersLoading] = useState(true);
  const [error, setError] = useState(false);

  const contentStyle = {
    display: "flex",
    width: "100%",
    mt: "1rem",
    flexDirection: mobileView || tabletView ? "column" : "row",
  };
  const bannerAreaStyle = {
    position: "relative",
    mb: bigScreen && (!mobileView || !tabletView) ? "3rem" : "2rem",
  };
  const topBannerStyle = {
    width: "100%",
    borderRadius: "1rem",
  };
  const bannerAreaStyleTablet = {
    position: "relative",
    mb: "1rem",
    borderRadius: "16px",
  };
  const emptyGPBannerStyle = {
    borderRadius: "1rem",
    mt: "0.75rem",
    width: "100%",
    height: "26rem",
    background: "grey",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    p: "0.5rem 0",
  };

  const currentDate = moment();
  const week = useRef(currentDate.format("YYYY-WW"));

  const emptyBannerStyle = {
    width: "100%",
    height: "10rem",
    background: "grey",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    borderRadius: "16px",
    p: "0.5rem 0",
  };

  // Initialize current parent to first parent in competition list
  useEffect(() => {
    competitionList?.length && setCurrCompetition(competitionList[0]);
  }, [competitionList]);

  // Get leaderboard for current competition
  useEffect(() => {
    const getLeaderboard = async () => {
      setRankings();
      setCurrTeam();
      setCurrTier();
      setFullUserTiers();
      if (cachedRankings[currCompetition.id]) {
        setRankings(cachedRankings[currCompetition.id]);
        return;
      }
      try {
        const data = await firebaseState.getLeaderboardForCompetition(
          currCompetition.id,
          week.current,
          userState.isMedidas ?? false,
          userState.profileID,
        );
        setCachedRankings((prevState) => ({
          ...prevState,
          [currCompetition.id]: data,
        }));
        setRankings(data);
        setCurrTier(Math.max(0, Object.keys(data).length - 1));
      } catch (e) {
        console.error(e);
        setError(true);
      }
    };
    currCompetition?.iteration && getLeaderboard();
  }, [currCompetition, userState]);

  // Set usertiers for competition
  useEffect(() => {
    const getTiers = () => {
      // Omit the global tier
      const tierKeys = Object.keys(rankings).slice(1);
      const parents = tierKeys.map((tier) => rankings[tier].parent);
      setFullUserTiers(
        parents.map((parent) => {
          const tier = parent.type;
          const score = rankings[tier][currStat.id].find(
            (team) => team.tgid === parent.id,
          );
          return { ...parent, score: score?.score };
        }),
      );
      if (!currTeam) setCurrTeam(parents[tierKeys.length - 1]);
      if (!currTier) setCurrTier(tierKeys.length);
    };
    rankings && currStat && getTiers();
  }, [rankings, currStat]);

  // Get banners for current competition
  useEffect(() => {
    const getBanners = async () => {
      setBannersLoading(true);
      const compBanners = await firebaseState.getCompBannerByCompId(
        currCompetition.iteration.id,
      );
      if (!compBanners) {
        setBanners({});
      } else {
        setBanners(compBanners);
      }
      setBannersLoading(false);
    };

    currCompetition?.iteration?.id && getBanners();
  }, [currCompetition, firebaseState]);

  // Switch banners on resolution change
  useEffect(() => {
    if (!banners) {
      return;
    }

    const currView = mobileView ? "mobile" : tabletView ? "tablet" : "desktop";

    let data;
    const { leaderboardBanners } = banners;

    if (leaderboardBanners && currTeam) {
      // TODO: Remove idGroupTL once removed from banners
      const teamBanner =
        banners.teamBanners[currTeam.idGroupTL] ??
        banners.teamBanners[currTeam.id];
      data = {
        main: leaderboardBanners[`${currView}Main`],
        prize: leaderboardBanners[`${currView}Prize`],
        team: teamBanner?.[currView],
      };
    } else {
      data = {
        main: banners[currView],
        prize: banners.ad,
        team: null,
      };
    }
    setCurrentBanners(data);
  }, [banners, currTeam, mobileView, tabletView]);

  useEffect(() => {
    const getSponsorLogos = async () => {
      let userSponsors = {};
      for (const curr of fullUserTiers) {
        if (currCompetition?.iteration?.sponsors[curr.id]) {
          const res = await firebaseState.getCompaniesByIds(
            currCompetition.iteration.sponsors[curr.id],
          );
          userSponsors[curr.id] = res.map((sponsor) => ({
            name: sponsor.companyName,
            logo: sponsor.sponsorLogoUrl || sponsor.iconUrl,
          }));
        }
      }
      setSponsorLogos(userSponsors);
    };
    fullUserTiers?.length && !sponsorLogos && getSponsorLogos();
  }, [currCompetition, fullUserTiers]);

  const changeCompetition = (comp) => {
    setCurrCompetition(comp);
    setError(false);
  };

  return !competitionList?.length || error ? (
    <Box id={SRC_NO_COMP_ERROR}>
      <ErrorNoCompPage
        currCompetition={currCompetition}
        setCurrCompetition={changeCompetition}
      />
    </Box>
  ) : rankings ? (
    <Box>
      <Box sx={bannerAreaStyle}>
        {currentBanners.main ? (
          <Box
            component="img"
            alt={t("bannerDesc")}
            src={currentBanners.main}
            width="100%"
            sx={topBannerStyle}
          />
        ) : (
          <Box sx={emptyBannerStyle}>
            {banners === undefined ? (
              <LoadingWheel width={4} />
            ) : (
              <Typography color={white} variant="h2">
                {"Error Loading Banner"}
              </Typography>
            )}
          </Box>
        )}
        {currTeam && (
          <Box sx={contentStyle}>
            <SRCLeaderboard
              rankings={rankings}
              fullUserTiers={fullUserTiers}
              currTeam={currTeam}
              setCurrTeam={setCurrTeam}
              currStat={currStat}
              setCurrStat={setCurrStat}
              currTier={currTier}
              setCurrTier={setCurrTier}
              sponsors={sponsorLogos}
              teamBanner={currentBanners?.team}
            />
            <GrandPrizeArea
              compData={rankings}
              currCompetition={currCompetition}
              setCurrCompetition={setCurrCompetition}
              gpBanner={currentBanners?.prize}
              bannersLoading={bannersLoading}
            />
          </Box>
        )}
      </Box>
      {(mobileView || tabletView) &&
        (currentBanners?.prize ? (
          <Box
            sx={bannerAreaStyleTablet}
            component="img"
            alt={t("bannerDesc")}
            src={currentBanners?.prize}
            width="100%"
          />
        ) : (
          <Box sx={emptyGPBannerStyle}>
            {banners === undefined ? (
              <LoadingWheel width={4} />
            ) : (
              <Typography color={white} variant="h2">
                {"Error Loading Banner"}
              </Typography>
            )}
          </Box>
        ))}
    </Box>
  ) : (
    <LoadingModal width={4} />
  );
};

export default SafeRoads;
